import { useEffect, useState } from "react";
import { Button, IconButton, InputAdornment, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MdDelete } from "react-icons/md";
import { IoSearchOutline } from "react-icons/io5";
import EditIcon from "@mui/icons-material/Edit";
import ConfirmDialog from "../../components/deleteModal";
import Loader from "../../components/loader";
import { editTenantBySuperAdmin, fetchTenants, removeTenantBySuperAdmin } from "../../store/slices/Auth/Login/thunk";
import { toast } from "react-toastify";
import { useClientFilter } from "../../Hooks/useClientFilter";
import FilterPopover from "../../components/FilterPopover/FilterPopover";

const TenantList = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [tenants, setTenants] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const pageSize = 10;
  const pageCount = Math.ceil(totalCount / pageSize);

  const { filteredData, filters, setFilter, clearFilter } = useClientFilter(tenants);

  useEffect(() => {
    const handler = setTimeout(async () => {
      setIsLoading(true);
      const response: any = await dispatch(fetchTenants(page, pageSize, search));
      if (response?.success) {
        setTenants(response.data?.data || []);
        setTotalCount(response.data?.totalCount || 0);
      } else {
        setTenants([]);
        setTotalCount(0);
      }
      setIsLoading(false);
    }, 400);

    return () => clearTimeout(handler);
  }, [dispatch, page, search]);

  const handleToggleStatus = async (tenant: any, checked: boolean) => {
    const response: any = await dispatch(
      editTenantBySuperAdmin({
        _id: tenant._id,
        isActive: checked,
      })
    );
    if (response?.success) {
      setTenants((prev) =>
        prev.map((item) =>
          item._id === tenant._id ? { ...item, isActive: checked } : item
        )
      );
      toast.success(`Tenant ${checked ? "activated" : "inactivated"} successfully`);
    } else {
      toast.error(response?.message || "Failed to update tenant status");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const response: any = await dispatch(removeTenantBySuperAdmin(deleteId));
    if (response?.success) {
      toast.success(response.message || "Tenant deleted");
      setTenants((prev) => prev.filter((item) => item._id !== deleteId));
    } else {
      toast.error(response?.message || "Failed to delete tenant");
    }
    setDeleteId(null);
    setOpenDialog(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: "20px", flexShrink: 0 }}>
        <div className="d-flex flex-column">
          <h2 style={{ color: "#0f172a", fontWeight: "700", fontSize: "24px", margin: "0", fontFamily: "sans-serif" }}>
            Tenant Management
          </h2>
          <p style={{ color: "#64748b", fontSize: "14px", margin: "4px 0 0 0", fontWeight: "400" }}>
            Manage companies, schools and organizations using Vel Web platform.
          </p>
        </div>
        <Button
          variant="contained"
          onClick={() => navigate("/create-tenant")}
          sx={{
            background: "#2563EB",
            color: "#fff",
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": { background: "#1976d2", boxShadow: "none" },
          }}
        >
          + Create Tenant
        </Button>
      </div>

      <div style={{ marginBottom: "16px", padding: "0px 16px 0px 0px" }}>
        <TextField
          variant="outlined"
          placeholder="Search tenant by name..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            minWidth: 380,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              backgroundColor: "#fff",
              height: "40px",
              "& fieldset": { borderColor: "#d0d5dd" },
              "&:hover fieldset": { borderColor: "#7c6dff" },
              "&.Mui-focused fieldset": { borderColor: "#7c6dff" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IoSearchOutline style={{ color: "#98a2b3", fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <Paper sx={{ padding: 2, borderRadius: "15px", flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <TableContainer sx={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 1 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ py: 0.5, px: 1 }}>S.No</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>Tenant Name<FilterPopover column="tenantName" filterType="text" currentValue={filters.tenantName || ""} onFilterChange={setFilter} onClear={clearFilter} /></TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>Tenant Id<FilterPopover column="tenantId" filterType="text" currentValue={filters.tenantId || ""} onFilterChange={setFilter} onClear={clearFilter} /></TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>Admin Name<FilterPopover column="adminName" filterType="text" currentValue={filters.adminName || ""} onFilterChange={setFilter} onClear={clearFilter} /></TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>Admin Email<FilterPopover column="adminEmail" filterType="text" currentValue={filters.adminEmail || ""} onFilterChange={setFilter} onClear={clearFilter} /></TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>Mode<FilterPopover column="tenantAppConfig.mode" filterType="text" currentValue={filters["tenantAppConfig.mode"] || ""} onFilterChange={setFilter} onClear={clearFilter} /></TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>Active / Inactive</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 0.5, px: 1 }}>
                      <Loader />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((tenant: any, index: number) => (
                    <TableRow key={tenant._id}>
                      <TableCell sx={{ py: 0.5, px: 1 }}>{(page - 1) * pageSize + index + 1}</TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>{tenant.tenantName}</TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>{tenant.tenantId}</TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>{tenant.adminName || "--"}</TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>{tenant.adminEmail || "--"}</TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>{tenant?.tenantAppConfig?.mode || "school"}</TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <Switch
                          checked={tenant.isActive !== false}
                          onChange={(e) => handleToggleStatus(tenant, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <IconButton onClick={() => navigate(`/edit-tenant/${tenant._id}`)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => { setDeleteId(tenant._id); setOpenDialog(true); }}>
                          <MdDelete style={{ color: "red" }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {!isLoading && filteredData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 0.5, px: 1 }}>
                      No tenant found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        <nav style={{ flexShrink: 0 }}>
          <ul className="pagination justify-content-center mt-3">
            <li className={`page-item ${page === 1 && "disabled"}`}>
              <button className="page-link" onClick={() => setPage((p) => Math.max(p - 1, 1))}>Previous</button>
            </li>
            {Array.from({ length: Math.min(5, pageCount || 1) }, (_, i) => (
              <li key={i + 1} className={`page-item ${page === i + 1 && "active"}`}>
                <button className="page-link" onClick={() => setPage(i + 1)}>{i + 1}</button>
              </li>
            ))}
            {pageCount > 5 && (
              <>
                <li className="page-item disabled">
                  <span className="page-link">...</span>
                </li>
                <li className="page-item">
                  <button className="page-link" onClick={() => setPage(pageCount)}>{pageCount}</button>
                </li>
              </>
            )}
            <li className={`page-item ${page === pageCount || pageCount === 0 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setPage((p) => Math.min(p + 1, pageCount || 1))}>Next</button>
            </li>
          </ul>
        </nav>
      </Paper>

      <ConfirmDialog open={openDialog} onClose={() => setOpenDialog(false)} onConfirm={handleDelete} />
    </div>
  );
};

export default TenantList;

